import { gql, useQuery } from "@apollo/client";
import { useMsal } from "@azure/msal-react";

/** A product (module) entry returned by the Identity GraphQL API. */
export interface TenantProduct {
  /** Backend module id. */
  moduleId: string;
  /** Backend module alias, e.g. "TOPCON_FLEET". */
  module: string;
  /** Human-readable module name. */
  name: string;
  /** Product URL for this tenant, or null when not contracted. */
  baseUrl: string | null;
  /** Whether the tenant has this product contracted. */
  contracted: boolean;
  /** Whether the current user has a UserModuleAccess for this module. */
  accessibleByCurrentUser: boolean;
}

export interface TenantProductsState {
  products: TenantProduct[];
  /** Tenant license expiration date (ISO string), or null when there is none. */
  licenseExpiresAt: string | null;
  loading: boolean;
  error: boolean;
}

interface TenantProductsQueryResult {
  tenant: {
    tenantProducts: {
      products: TenantProduct[];
      licenseExpiresAt: string | null;
    } | null;
  };
}

const TENANT_PRODUCTS_QUERY = gql`
  query tenantProducts {
    tenant {
      tenantProducts {
        products {
          moduleId
          module
          name
          baseUrl
          contracted
          accessibleByCurrentUser
        }
        licenseExpiresAt
      }
    }
  }
`;

/**
 * Fetches the authenticated tenant's product list from the Identity GraphQL
 * API. The backend derives the tenant from the JWT's `extension_TenantId`
 * claim, so the query takes no arguments. Apollo handles authentication
 * (via the link's `customFetch`), caching and error reporting.
 */
export const useTenantProducts = (): TenantProductsState => {

  const { instance } = useMsal();

  const isAuthenticated = Boolean(instance.getActiveAccount());

  const { data, loading, error } = useQuery<TenantProductsQueryResult>(
    TENANT_PRODUCTS_QUERY,
    { skip: !isAuthenticated }
  );

  const result = data?.tenant?.tenantProducts;

  return {
    products: result?.products ?? [],
    licenseExpiresAt: result?.licenseExpiresAt ?? null,
    loading,
    error: Boolean(error)
  };
};
